/**
 * The app's general-purpose labeled form input, driven by a FieldConfig object.
 * Renders a textarea, a number input (with integerOnly digit filtering or
 * useTwoDecimalPlaces currency-style handling, both clamping to min/max on
 * blur), or a text/tel/email input (tel values are formatted for display).
 * Extras: readonly and pasteOnly modes, character limits with optional counter,
 * selection callbacks, and an optional alert/info accessory icon with tooltip
 * via FieldAccessoryConfig. Used by most forms in the app: bounty create/edit
 * and display, submission create/display, account/profile, and admin AI pages.
 */
import React from "react";
import { AlertCircle, InfoIcon } from "lucide-react";
import { formatPhoneNumberForDisplay } from "@/utils/user";

type InputType = "text" | "tel" | "email" | "textarea" | "number";
type AccessoryType = "alert" | "info";

export interface FieldConfig {
    key: string;
    label: string;
    type: InputType;
    readonly: boolean;
    readonlyValue?: any | null;
    fieldState?: any | null;
    setFieldState?: React.Dispatch<React.SetStateAction<any>> | null;
    placeholder?: string;
    min?: number;
    max?: number;
    integerOnly?: boolean;
    useTwoDecimalPlaces?: boolean;
    pasteOnly?: boolean;
}

export interface FieldAccessoryConfig {
    type: AccessoryType;
    forType: InputType;
    tooltipMessage?: string;
    onClick?: () => void;
}

export type InputFieldProps = {
    fieldConfig: FieldConfig;
    accessoryConfig?: FieldAccessoryConfig | null;
    className?: string;
    height?: string;
    characterLimit?: number;
    showCharacterCount?: boolean;
    onSelect?: (selectionStart: number, selectionEnd: number) => void;
};

const BASE_INPUT_CLASSES =
    "w-full px-2 py-2 border rounded-lg focus:border-black focus:outline-none tracking-wider text-sm disabled:bg-white";

const InputField: React.FC<InputFieldProps> = ({
    fieldConfig,
    accessoryConfig,
    className,
    height,
    characterLimit,
    showCharacterCount,
    onSelect,
}) => {
    const {
        key,
        label,
        type: inputType,
        readonly,
        readonlyValue,
        fieldState,
        setFieldState,
        placeholder,
        min,
        max,
        integerOnly,
        useTwoDecimalPlaces,
        pasteOnly,
    } = fieldConfig;
    const { type: accessoryType, forType, tooltipMessage } = accessoryConfig || {};
    const [tooltipVisible, setTooltipVisible] = React.useState<boolean>(false);

    // Track display value separately for decimal inputs to avoid clearing during typing
    const [internalValue, setInternalValue] = React.useState<string>("");
    const [integerInputValue, setIntegerInputValue] = React.useState<string>("");
    const inputRef = React.useRef<HTMLInputElement>(null);
    const isUserTyping = React.useRef<boolean>(false);
    const isPasteOnly = Boolean(pasteOnly);

    // Update internal value when fieldState changes (e.g., loading new data)
    React.useEffect(() => {
        if (useTwoDecimalPlaces && !isUserTyping.current) {
            const newValue =
                fieldState !== null && fieldState !== undefined && fieldState !== ""
                    ? typeof fieldState === "string"
                        ? fieldState
                        : String(fieldState)
                    : "";

            // Only update if the value actually changed to avoid unnecessary re-renders
            if (newValue !== internalValue) {
                setInternalValue(newValue);
            }
        }
    }, [fieldState, useTwoDecimalPlaces, internalValue]); // Update when fieldState changes

    // Update integer input value when fieldState changes (e.g., loading new data)
    React.useEffect(() => {
        if (integerOnly && !isUserTyping.current) {
            const newValue =
                fieldState !== null && fieldState !== undefined && fieldState !== ""
                    ? String(fieldState)
                    : "";
            if (newValue !== integerInputValue) {
                setIntegerInputValue(newValue);
            }
        }
    }, [fieldState, integerOnly, integerInputValue]);

    function keepWithinCharacterLimit(val: string) {
        if (characterLimit && val.length > characterLimit) {
            return fieldState.slice(0, characterLimit);
        }
        return val;
    }

    return (
        <div key={key} className={`w-full flex flex-col font-porsui text-slate-950 ${className}`}>
            <div className="flex items-center justify-between mb-1">
                <label htmlFor={key} className="block text-sm font-medium tracking-widest">
                    {label}
                </label>
                {showCharacterCount && characterLimit && inputType === "textarea" && (
                    <span className="text-xs text-slate-400 tracking-wider">
                        {(readonlyValue || fieldState || "").length}/{characterLimit}
                    </span>
                )}
            </div>
            {inputType === "textarea" ? (
                <textarea
                    id={key}
                    value={readonlyValue || fieldState || ""}
                    rows={3}
                    className={`${height ?? "h-32"} ${BASE_INPUT_CLASSES} resize-none`}
                    placeholder={placeholder || ""}
                    readOnly={readonly}
                    tabIndex={0}
                    onChange={(e) => {
                        setFieldState!(keepWithinCharacterLimit(e.target.value));
                    }}
                    onMouseUp={(e) => {
                        if (onSelect) {
                            const target = e.target as HTMLTextAreaElement;
                            onSelect(target.selectionStart, target.selectionEnd);
                        }
                    }}
                    onKeyUp={(e) => {
                        if (onSelect) {
                            const target = e.target as HTMLTextAreaElement;
                            onSelect(target.selectionStart, target.selectionEnd);
                        }
                    }}
                    onSelect={(e) => {
                        if (onSelect) {
                            const target = e.target as HTMLTextAreaElement;
                            onSelect(target.selectionStart, target.selectionEnd);
                        }
                    }}
                />
            ) : inputType == "number" ? (
                integerOnly ? (
                    <input
                        id={key}
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={readonlyValue ?? integerInputValue ?? ""}
                        className={`${height ?? "h-10"} ${BASE_INPUT_CLASSES} bg-white`}
                        readOnly={readonly}
                        disabled={readonly}
                        tabIndex={readonly ? -1 : 0}
                        onFocus={() => {
                            isUserTyping.current = true;
                        }}
                        onChange={(e) => {
                            isUserTyping.current = true;
                            const digitsOnly = (e.target.value || "").replace(/\D/g, "");
                            setIntegerInputValue(digitsOnly);
                            if (digitsOnly === "") {
                                setFieldState!(null);
                                return;
                            }
                            // Store raw value during typing, validate on blur
                            const num = Number(digitsOnly);
                            setFieldState!(num);
                        }}
                        onBlur={(e) => {
                            isUserTyping.current = false;
                            const digitsOnly = (e.target.value || "").replace(/\D/g, "");
                            if (digitsOnly === "") {
                                setIntegerInputValue("");
                                setFieldState!(null);
                                return;
                            }
                            let num = Number(digitsOnly);
                            // Apply min/max constraints only on blur
                            if (typeof min === "number" && num < min) num = min;
                            if (typeof max === "number" && num > max) num = max;
                            setIntegerInputValue(String(num));
                            setFieldState!(num);
                        }}
                    />
                ) : useTwoDecimalPlaces ? (
                    <input
                        ref={inputRef}
                        id={key}
                        type="text"
                        inputMode="decimal"
                        value={readonly ? (readonlyValue ?? fieldState ?? "") : internalValue}
                        className={`${height ?? "h-10"} ${BASE_INPUT_CLASSES} bg-white`}
                        readOnly={readonly}
                        disabled={readonly}
                        tabIndex={readonly ? -1 : 0}
                        onFocus={() => {
                            isUserTyping.current = true;
                        }}
                        onChange={(e) => {
                            isUserTyping.current = true;
                            let raw = e.target.value.replace(/,/g, "");
                            raw = raw.replace(/[^0-9.]/g, "");

                            // Handle leading dot
                            if (raw.startsWith(".")) raw = "0.";

                            // Only keep first dot
                            const dotIndex = raw.indexOf(".");
                            if (dotIndex !== -1) {
                                raw =
                                    raw.slice(0, dotIndex + 1) +
                                    raw.slice(dotIndex + 1).replace(/\./g, "");
                                // Limit to 2 decimal places
                                const [intPart, decPart] = raw.split(".");
                                if (decPart && decPart.length > 2) {
                                    raw = intPart + "." + decPart.slice(0, 2);
                                }
                            }

                            // Update React state (controlled)
                            setInternalValue(raw);

                            // Update parent state with sanitized value
                            if (raw === "") {
                                setFieldState!(null);
                            } else {
                                // Allow trailing dot during typing
                                setFieldState!(raw);
                            }
                        }}
                        onBlur={(e) => {
                            const val = e.target.value;

                            // Parse final value or set to null if empty/invalid
                            if (val === "" || val === "0.") {
                                setInternalValue("");
                                setFieldState!(null);
                                isUserTyping.current = false;
                                return;
                            }

                            const num = parseFloat(val);
                            if (isNaN(num)) {
                                // Invalid number - clear it
                                setInternalValue("");
                                setFieldState!(null);
                                isUserTyping.current = false;
                                return;
                            }

                            // Apply min/max clamping
                            let clamped = num;
                            if (typeof min === "number" && clamped < min) {
                                clamped = min;
                            }
                            if (typeof max === "number" && clamped > max) {
                                clamped = max;
                            }

                            // Only format to .00 if user explicitly typed a decimal point
                            const formatted = val.includes(".")
                                ? clamped.toFixed(2)
                                : String(clamped);

                            // Update state with formatted value
                            setInternalValue(formatted);
                            setFieldState!(formatted);
                            isUserTyping.current = false;
                        }}
                    />
                ) : (
                    <input
                        id={key}
                        type="number"
                        min={min!}
                        max={max!}
                        step="1"
                        value={readonlyValue ?? fieldState ?? ""}
                        className={`${height ?? "h-10"} ${BASE_INPUT_CLASSES} bg-white`}
                        readOnly={readonly}
                        disabled={readonly}
                        tabIndex={readonly ? -1 : 0}
                        onChange={(e) => {
                            const val = e.target.value === "" ? null : Number(e.target.value);
                            setFieldState!(val);
                        }}
                    />
                )
            ) : (
                <div className="relative">
                    <input
                        id={key}
                        type={inputType}
                        value={
                            inputType === "tel"
                                ? formatPhoneNumberForDisplay(readonlyValue || fieldState || "")
                                : readonlyValue || fieldState || ""
                        }
                        className={`${height ?? "h-10"} ${BASE_INPUT_CLASSES}`}
                        placeholder={placeholder || ""}
                        readOnly={readonly || isPasteOnly}
                        disabled={readonly}
                        tabIndex={readonly && !isPasteOnly ? -1 : 0}
                        onKeyDown={(e) => {
                            if (isPasteOnly) {
                                // Allow Tab, Backspace, Delete, navigation keys, and keyboard shortcuts (Ctrl/Cmd combinations)
                                const isAllowedKey =
                                    e.key === "Tab" ||
                                    e.key === "Backspace" ||
                                    e.key === "Delete" ||
                                    e.key === "ArrowLeft" ||
                                    e.key === "ArrowRight" ||
                                    e.key === "ArrowUp" ||
                                    e.key === "ArrowDown" ||
                                    e.key === "Home" ||
                                    e.key === "End" ||
                                    e.key === "PageUp" ||
                                    e.key === "PageDown" ||
                                    e.ctrlKey ||
                                    e.metaKey; // metaKey is Cmd on Mac

                                if (!isAllowedKey) {
                                    e.preventDefault();
                                }
                            }
                        }}
                        onChange={(e) => {
                            setFieldState!(keepWithinCharacterLimit(e.target.value));
                        }}
                        onPaste={(e) => {
                            if (!isPasteOnly) {
                                return;
                            }
                            e.preventDefault();
                            const clipboardText =
                                e.clipboardData?.getData("text") ??
                                // @ts-expect-error - IE fallback
                                (window.clipboardData?.getData?.("Text") as string | undefined) ??
                                "";
                            setFieldState!(
                                keepWithinCharacterLimit(
                                    clipboardText === null ? "" : clipboardText
                                )
                            );
                        }}
                    />
                    {accessoryConfig && inputType == forType && (
                        <div className="absolute inset-y-0 right-0 flex items-center mr-2">
                            {(accessoryType === "alert" && (
                                <AlertCircle
                                    className="w-5 h-5 cursor-pointer text-red-600"
                                    onMouseEnter={() => setTooltipVisible(true)}
                                    onMouseLeave={() => setTooltipVisible(false)}
                                    onClick={() => {
                                        accessoryConfig.onClick?.();
                                    }}
                                />
                            )) ||
                                (accessoryType === "info" && (
                                    <InfoIcon
                                        className="w-5 h-5 cursor-pointer text-slate-950"
                                        onMouseEnter={() => setTooltipVisible(true)}
                                        onMouseLeave={() => setTooltipVisible(false)}
                                    />
                                ))}

                            {tooltipMessage && tooltipVisible && (
                                <div className="absolute bottom-full right-0 mb-2 w-64 p-2 bg-slate-950 text-white text-sm rounded shadow-lg z-50 whitespace-pre-line">
                                    {tooltipMessage}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default InputField;
